import { c as _c } from "react/compiler-runtime";
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import { useEntityContext } from '@/entities/components/details/hooks/use-entity-context';
import type { SectionKey } from '../../../../onboardings.types';
type Section = {
  key: SectionKey;
  title: string;
  isShown: boolean;
};

/** The sections of an onboarding, in order: what was collected, why it was decided, then context. */
const useSections = onboarding => {
  const $ = _c(52);
  const {
    t
  } = useTranslation("entity-details");
  const {
    kind: entityKind
  } = useEntityContext();
  const isPerson = entityKind === "person";
  const isBusiness = entityKind === "business";
  const isCompleteKybOnboarding = onboarding.kind === "kyb" && (onboarding.status === "pass" || onboarding.status === "fail");
  const hasBusinessInsights = isBusiness && isCompleteKybOnboarding && Boolean(onboarding.seqno);
  let t0;
  if ($[0] !== t) {
    t0 = t(_temp);
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== isPerson || $[3] !== t0) {
    t1 = {
      key: "user-data",
      title: t0,
      isShown: isPerson
    };
    $[2] = isPerson;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t(_temp2);
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isBusiness || $[8] !== t2) {
    t3 = {
      key: "business-data",
      title: t2,
      isShown: isBusiness
    };
    $[7] = isBusiness;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t(_temp3);
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = onboarding.kind !== "document";
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      key: "rules",
      title: t4,
      isShown: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t(_temp4);
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = {
      key: "risk-signals",
      title: t7,
      isShown: true
    };
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t(_temp5);
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isPerson || $[22] !== t9) {
    t10 = {
      key: "field-validations",
      title: t9,
      isShown: isPerson
    };
    $[21] = isPerson;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t(_temp6);
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isPerson || $[27] !== t11) {
    t12 = {
      key: "duplicate-data",
      title: t11,
      isShown: isPerson
    };
    $[26] = isPerson;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t) {
    t13 = t(_temp7);
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== hasBusinessInsights || $[32] !== t13) {
    t14 = {
      key: "business-insights",
      title: t13,
      isShown: hasBusinessInsights
    };
    $[31] = hasBusinessInsights;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t(_temp8);
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = {
      key: "device-insights",
      title: t15,
      isShown: true
    };
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t(_temp9);
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = {
      key: "other-insights",
      title: t17,
      isShown: true
    };
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t1 || $[43] !== t10 || $[44] !== t12 || $[45] !== t14 || $[46] !== t16 || $[47] !== t18 || $[48] !== t3 || $[49] !== t6 || $[50] !== t8) {
    const sections = [t1, t3, t6, t8, t10, t12, t14, t16, t18];
    t19 = sections.filter(_temp10);
    $[42] = t1;
    $[43] = t10;
    $[44] = t12;
    $[45] = t14;
    $[46] = t16;
    $[47] = t18;
    $[48] = t3;
    $[49] = t6;
    $[50] = t8;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
export default useSections;
function _temp($) {
  return $.onboardings.userData.title;
}
function _temp2($_0) {
  return $_0.onboardings.businessData.title;
}
function _temp3($_1) {
  return $_1.onboardings.rules.title;
}
function _temp4($_2) {
  return $_2.onboardings.riskSignals.title;
}
function _temp5($_3) {
  return $_3.onboardings.tabs.validations;
}
function _temp6($_4) {
  return $_4.onboardings.tabs.duplicates;
}
function _temp7($_5) {
  return $_5.onboardings.businessShared.title;
}
function _temp8($_6) {
  return $_6.onboardings.tabs.devices;
}
function _temp9($_7) {
  return $_7.otherInsights.title;
}
function _temp10(section) {
  return section.isShown;
}